// GraphQL API Types for commit.you

export type ReactionType = 'HEART' | 'CELEBRATION' | 'FIRE' | 'SLEEP' | 'DYNAMITE';
export type CommitType = string;

export interface Badge {
  type: string;
  awardedAt: string;
}

export interface UserBranch {
  branch: string;
  commitCount: number;
  sortOrder: number;
  commitSortDirection: 'asc' | 'desc';
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  username: string;
  email?: string;
  displayName?: string;
  avatarUrl?: string;
  bio?: string;
  location?: string;
  githubUrl?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
  blueskyUrl?: string;
  portfolioUrl?: string;
  badges?: Badge[];
  followerCount: number;
  followingCount: number;
  commitCount: number;
  branches?: UserBranch[];
  isFollowing?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

// Takeaway item types
export type TakeawayItemType = 'do' | 'dont' | 'idea';

export interface TakeawayItem {
  type: TakeawayItemType;
  text: string;
}

export interface CommitTakeaways {
  items: TakeawayItem[];
}

export interface CommitSnippet {
  before: string;
  after?: string;
  language?: string;
}

export interface Mention {
  userId: string;
  user?: User;
  position: number;
}

export interface ReactionCounts {
  heart: number;
  celebration: number;
  fire: number;
  sleep: number;
  dynamite: number;
}

export interface Commit {
  id: string;
  userId: string;
  user?: User;
  type?: CommitType;
  branch: string;
  message: string;
  body?: string;
  takeaways?: CommitTakeaways;
  snippet?: CommitSnippet;
  tags: string[];
  mentions?: Mention[];
  reactionCounts: ReactionCounts;
  userReactions?: ReactionType[];
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Comment {
  id: string;
  commitId: string;
  userId: string;
  user?: User;
  parentId?: string;
  body: string;
  mentions?: Mention[];
  replyCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Reaction {
  id: string;
  commitId: string;
  userId: string;
  user?: User;
  type: ReactionType;
  createdAt: string;
}

export interface Follow {
  followerId: string;
  followingId: string;
  createdAt: string;
}

export interface Collection {
  id: string;
  userId: string;
  name: string;
  description?: string;
  commitIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface LinkedInPost {
  title: string;
  body: string;
  url: string;
}

export interface CommitConnection {
  items: Commit[];
  nextToken?: string;
}

export interface CommentConnection {
  items: Comment[];
  nextToken?: string;
}

// Input types
export interface CreateCommitInput {
  type?: CommitType;
  branch?: string;
  message: string;
  body?: string;
  takeaways?: CommitTakeawaysInput;
  snippet?: CommitSnippetInput;
  tags?: string[];
}

export interface UpdateCommitInput {
  type?: CommitType;
  branch?: string;
  message?: string;
  body?: string;
  takeaways?: CommitTakeawaysInput;
  snippet?: CommitSnippetInput;
  tags?: string[];
}

export interface TakeawayItemInput {
  type: TakeawayItemType;
  text: string;
}

export interface CommitTakeawaysInput {
  items?: TakeawayItemInput[];
}

export interface CommitSnippetInput {
  before: string;
  after?: string;
  language?: string;
}

export interface UpdateProfileInput {
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  bio?: string;
  location?: string;
  githubUrl?: string;
  linkedinUrl?: string;
  twitterUrl?: string;
  blueskyUrl?: string;
  portfolioUrl?: string;
}

export interface AvatarUploadUrl {
  uploadUrl: string;
  avatarUrl: string;
}

export interface CreateCollectionInput {
  name: string;
  description?: string;
}

export interface UpdateBranchSettingsInput {
  branch: string;
  sortOrder?: number;
  commitSortDirection?: 'asc' | 'desc';
}

export interface BranchOrderInput {
  branch: string;
  sortOrder: number;
}

export interface ReorderBranchesInput {
  branches: BranchOrderInput[];
}

// Shared constants

/** Maps ReactionType enum values to ReactionCounts field names */
export const REACTION_FIELD_MAP: Record<ReactionType, keyof ReactionCounts> = {
  HEART: 'heart',
  CELEBRATION: 'celebration',
  FIRE: 'fire',
  SLEEP: 'sleep',
  DYNAMITE: 'dynamite',
};

/** Reaction display configuration */
export const REACTIONS: { type: ReactionType; emoji: string; label: string }[] = [
  { type: 'HEART', emoji: '♥️', label: 'Love' },
  { type: 'CELEBRATION', emoji: '🎉', label: 'Celebrate' },
  { type: 'FIRE', emoji: '🔥', label: 'Fire' },
  { type: 'SLEEP', emoji: '💤', label: 'Meh' },
  { type: 'DYNAMITE', emoji: '🧨', label: 'Mind-blown' },
];

/** Takeaway item configuration */
export const TAKEAWAY_CONFIG: Record<TakeawayItemType, { symbol: string; color: string }> = {
  do: { symbol: '👍', color: 'text-green-500' },
  dont: { symbol: '👎', color: 'text-red-500' },
  idea: { symbol: '💡', color: 'text-amber-500' },
};
